import { useEffect, useRef } from 'react';

export interface UseKeyDownOptions {
  /** The key(s) to listen for. Can be a single key or array of keys (e.g., 'Enter' or ['Enter', 'Space']) */
  key: string | string[];
  /** Whether the hook is currently enabled */
  enabled?: boolean;
  /** Whether to prevent default behavior when key is pressed */
  preventDefault?: boolean;
  /** Optional callback fired before the main callback */
  onBeforeCallback?: () => void;
  /** Optional selector to scope the listener to a specific container */
  containerSelector?: string;
  /** Whether to allow the key press in textarea elements */
  allowInTextarea?: boolean;
  /** Whether to allow the key press when modifier keys are pressed */
  allowWithModifiers?: boolean;
  /** Specific element types to exclude (e.g., ['TEXTAREA', 'SELECT']) */
  excludeElements?: string[];
}

/**
 * Hook that listens for specific key presses and executes a callback
 *
 * @param callback - Function to execute when the specified key is pressed (can be async)
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * // Listen for Enter key to submit form
 * useKeyDown(handleSubmit, {
 *   key: 'Enter',
 *   enabled: !isSubmitting,
 *   allowInTextarea: false,
 * });
 *
 * // Listen for Escape key to close modal
 * useKeyDown(closeModal, {
 *   key: 'Escape',
 *   containerSelector: '.modal',
 * });
 *
 * // Listen for multiple keys
 * useKeyDown(handleNavigation, {
 *   key: ['ArrowLeft', 'ArrowRight'],
 *   enabled: true,
 * });
 * ```
 */
export const useKeyDown = (callback: (event: KeyboardEvent) => void | Promise<void>, options: UseKeyDownOptions) => {
  const {
    key,
    enabled = true,
    preventDefault = true,
    onBeforeCallback,
    containerSelector,
    allowInTextarea = false,
    allowWithModifiers = false,
    excludeElements = [],
  } = options;

  const callbackRef = useRef(callback);
  const onBeforeCallbackRef = useRef(onBeforeCallback);

  // Keep refs up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    onBeforeCallbackRef.current = onBeforeCallback;
  }, [onBeforeCallback]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the pressed key matches
      const keysToMatch = Array.isArray(key) ? key : [key];
      if (!keysToMatch.includes(event.key)) {
        return;
      }

      // Check if we should exclude based on target element
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName.toUpperCase();

        // Check textarea exclusion
        if (!allowInTextarea && tagName === 'TEXTAREA') {
          return;
        }

        // Check custom element exclusions
        if (excludeElements.length > 0 && excludeElements.includes(tagName)) {
          return;
        }
      }

      // Check modifier keys
      if (!allowWithModifiers) {
        if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
          return;
        }
      }

      // Prevent default if requested
      if (preventDefault) {
        event.preventDefault();
      }

      // Call before callback if provided
      if (onBeforeCallbackRef.current) {
        onBeforeCallbackRef.current();
      }

      // Trigger the main callback (handle both sync and async)
      void callbackRef.current(event);
    };

    // Determine the element to attach the listener to
    const element = containerSelector ? document.querySelector(containerSelector) : document;

    if (!element) {
      console.warn(`useKeyDown: Container with selector "${containerSelector}" not found`);
      return;
    }

    element.addEventListener('keydown', handleKeyDown as EventListener);

    return () => {
      element.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [enabled, key, preventDefault, containerSelector, allowInTextarea, allowWithModifiers, excludeElements]);
};
